import { useId, useMemo, useState } from 'react'
import { Area, AreaChart, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts'
import { ChevronDown } from 'lucide-react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { BankerAumPayload } from '../../contracts'
import { DemoDataChip } from './DemoDataChip'
import {
  BANKER_PERFORMANCE_DEFAULT_RANGE,
  BANKER_PERFORMANCE_RANGES,
  buildBankerPerformanceSeries,
  resolveBankerAnchorPct,
  type BankerPerformanceRange,
} from './bankerPerformanceSeries'
import styles from './BankerAumCard.module.scss'

const compactEurFormatter = new Intl.NumberFormat('it-IT', {
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
})

const pctFormatter = new Intl.NumberFormat('it-IT', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
})

function formatCompactEuro(value: number | null | undefined): string {
  const v = typeof value === 'number' && Number.isFinite(value) ? value : 0
  const abs = Math.abs(v)
  if (abs >= 1_000_000_000) {
    return `€ ${compactEurFormatter.format(v / 1_000_000_000)} Mld`
  }
  if (abs >= 1_000_000) {
    return `€ ${compactEurFormatter.format(v / 1_000_000)} M`
  }
  if (abs >= 1_000) {
    return `€ ${compactEurFormatter.format(v / 1_000)} K`
  }
  return `€ ${compactEurFormatter.format(v)}`
}

function formatPctSigned(value: number): string {
  const formatted = pctFormatter.format(Math.abs(value))
  return `${value >= 0 ? '+' : '−'}${formatted}%`
}

interface BankerAumCardProps {
  aum: BankerAumPayload | null
  loading?: boolean
  className?: string
}

export function BankerAumCard({ aum, loading = false, className }: BankerAumCardProps) {
  const [range, setRange] = useState<BankerPerformanceRange>(BANKER_PERFORMANCE_DEFAULT_RANGE)
  const [popoverOpen, setPopoverOpen] = useState(false)

  // Series and the period's headline % are derived from a per-banker seed so
  // every banker sees a stable demo dataset. YTD prefers the real number from
  // the backend; every other window stays mocked until a history endpoint
  // exists. Recomputed only when the underlying inputs change.
  const seedKey = aum?.advisorCode || 'banker:fallback'
  const latestRefDt = aum?.latestRefDt ?? null
  const realYtdPct = aum?.ytdPct ?? null

  const anchorPct = useMemo(
    () => resolveBankerAnchorPct(range, seedKey, realYtdPct),
    [range, seedKey, realYtdPct],
  )
  const series = useMemo(
    () => buildBankerPerformanceSeries(seedKey, range, anchorPct, latestRefDt),
    [seedKey, range, anchorPct, latestRefDt],
  )

  const isLoading = loading || aum === null
  const positive = anchorPct >= 0
  const accentVar = positive
    ? 'var(--bank-color-sentiment-positive)'
    : 'var(--bank-color-sentiment-negative)'

  return (
    <aside
      className={[styles.root, className].filter(Boolean).join(' ')}
      aria-label="AUM banker"
      aria-busy={isLoading ? true : undefined}
    >
      <div className={styles.chart} aria-hidden>
        {/*
          Only mount the chart once real data has resolved. Rendering it
          during the loading state would mean its mount animation plays
          immediately against the placeholder, finishing off-screen before
          the user can perceive it; the real series then snaps in without
          a reveal. Gating on `!isLoading` makes the recharts mount + draw
          animation always coincide with the data becoming visible.
        */}
        {isLoading ? null : <BankerAumChart series={series} accentVar={accentVar} />}
      </div>

      <div className={styles.body}>
        {isLoading ? (
          <>
            <span className={styles.placeholderValue} aria-hidden />
            <span className={styles.placeholderDelta} aria-hidden />
          </>
        ) : (
          <>
            <p className={styles.value}>{formatCompactEuro(aum?.totalEur)}</p>
            <span
              className={[styles.deltaRow, positive ? null : styles.deltaRowNegative]
                .filter(Boolean)
                .join(' ')}
            >
              {/*
                The card no longer has an eyebrow row to host the mock marker,
                but the chart series and non-YTD performance are still mocked,
                so the demo-data chip has to live somewhere visible. Tight
                inline before the delta arrow keeps the registry entry alive
                without re-introducing the header band.
              */}
              <DemoDataChip
                field="banker.aum.performance_history"
                note="Storico AUM giornaliero del banker e performance per periodi diversi da YTD non ancora disponibili dalla sorgente dati."
                tight
              />
              <span
                className={[styles.deltaIcon, positive ? null : styles.deltaIconNegative]
                  .filter(Boolean)
                  .join(' ')}
                aria-hidden
              />
              <span>{formatPctSigned(anchorPct)}</span>
              <PeriodChip
                range={range}
                onRangeChange={setRange}
                open={popoverOpen}
                onOpenChange={setPopoverOpen}
              />
            </span>
          </>
        )}
      </div>
    </aside>
  )
}

interface PeriodChipProps {
  range: BankerPerformanceRange
  onRangeChange: (range: BankerPerformanceRange) => void
  open: boolean
  onOpenChange: (open: boolean) => void
}

function PeriodChip({ range, onRangeChange, open, onOpenChange }: PeriodChipProps) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={styles.periodChip}
          aria-label={`Periodo selezionato: ${range}. Cambia periodo`}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span>{range}</span>
          <ChevronDown className={styles.periodChipIcon} aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className={styles.periodPopover}
        role="listbox"
        aria-label="Periodo performance"
      >
        {BANKER_PERFORMANCE_RANGES.map((option) => {
          const selected = option === range
          return (
            <button
              key={option}
              type="button"
              role="option"
              aria-selected={selected}
              className={[styles.periodOption, selected ? styles.periodOptionSelected : null]
                .filter(Boolean)
                .join(' ')}
              onClick={() => {
                onRangeChange(option)
                onOpenChange(false)
              }}
            >
              {option}
            </button>
          )
        })}
      </PopoverContent>
    </Popover>
  )
}

interface BankerAumChartPoint {
  iso: string
  value: number
  tooltipLabel: string
}

interface BankerAumChartProps {
  series: ReadonlyArray<BankerAumChartPoint>
  accentVar: string
}

function BankerAumChart({ series, accentVar }: BankerAumChartProps) {
  const gradientId = useId()
  if (series.length < 2) return null
  // Recharts' data prop is invariant on the array — clone the readonly slice
  // into a mutable array at the boundary so we can keep the producing type
  // readonly without leaking the cast into the chart body.
  const data: BankerAumChartPoint[] = series.slice()

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accentVar} stopOpacity={0.32} />
            <stop offset="100%" stopColor={accentVar} stopOpacity={0} />
          </linearGradient>
        </defs>
        <RechartsTooltip
          cursor={{ stroke: 'var(--border)' }}
          formatter={(value: number) => [`${formatPctSigned(value)}`, 'Cum.']}
          labelFormatter={(_label, payload) => {
            const point = payload?.[0]?.payload as { tooltipLabel?: string } | undefined
            return point?.tooltipLabel ?? ''
          }}
          contentStyle={{
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            font: 'var(--bank-text-caption)',
            padding: '4px 8px',
          }}
          labelStyle={{ color: 'var(--muted-foreground)' }}
          itemStyle={{ color: 'var(--foreground)' }}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={accentVar}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          dot={false}
          activeDot={{ r: 3, fill: accentVar, stroke: 'var(--card)', strokeWidth: 2 }}
          // Recharts' built-in mount animation: the line is revealed
          // left-to-right via stroke-dasharray while the area fill grows in
          // underneath. Replays whenever the data prop changes, which gives
          // a free transition between periods when the chip is cycled.
          isAnimationActive
          animationBegin={120}
          animationDuration={900}
          animationEasing="ease-out"
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}
